// MaintenanceSalaryCombinationV2Module.js
import React, { useState } from "react";
import MaintenanceSalaryCombinationV2Table from "./components/MaintenanceSalaryCombinationV2Table";
import OperationBenefitGroupTable from "./components/OperationBenefitGroupTable";
import RateAddEditModal from "./components/RateAddEditModal";
import OperationBenefitsAddEditModal from "./components/OperationBenefitsAddEditModal";
import { Button } from "../../../../common/APICommon";
import AddCardIcon from '@mui/icons-material/AddCard';
import LayersIcon from '@mui/icons-material/Layers';
import PostAddIcon from '@mui/icons-material/PostAdd';
import EditIcon from '@mui/icons-material/Edit';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { DynamicFilterBar } from "../../filters/DynamicFilterBar";
import { FEATURES } from "../../../../config/features";
import { useOperationBenefitGroups } from "../../../hooks/useOperationBenefitGroups";
import OperationBenefitGroupModal from "./components/OperationBenefitGroupModal";


const initialModalState = {
  isOpen: false,
  mode: "Add"
};

const MaintenanceSalaryCombinationV2Module = ({
  user,
  rules, // Standard Rate rules from hook
  benefitGroups, // Grouped benefits from hook
  loading,
  refresh,
  saveRateRule,
  saveBenefit,
  showGeneralMessage,
  filterManager
}) => {
  const {
    activeFilterState,
    onFilterChange,
    applyFilters,
    resetFilters,
    getFiltersAsDetails
  } = filterManager;

  const {
    selectOptions: groupOptions,
    loading: groupsLoading,
    saveGroup
  } = useOperationBenefitGroups(user, showGeneralMessage); // MODIFIED: Added user for audit

  // -- Modal States --
  const [rateModal, setRateModal] = useState(initialModalState);
  const [operationBenefitModal, setOperationBenefitModal] = useState(initialModalState);
  const [operationBenefitGroupModal, setOperationBenefitGroupModalOpen] = useState(initialModalState);
  const [selectedRateItem, setSelectedRateItem] = useState(null);
  const [selectedBenefitItem, setSelectedBenefitItem] = useState(null);
  const [selectedOperationBenefitGroupItem, setSelectedOperationBenefitGroupItem] = useState(null);

  const [currentRatePage, setCurrentRatePage] = useState(1);

  // -- Rate Handlers --
  const handleRateEdit = (item) => {
    setSelectedRateItem(item);
    setRateModal({ isOpen: true, mode: "Edit" });
  };

  const handleRateSave = async (form) => {
    const res = await saveRateRule(selectedRateItem?.id, form, user.id);
    if (res.success) setRateModal(initialModalState);
  };

  const handleOpenRateModal = () => {
    const selectedFilterKeys = Object.keys(activeFilterState).filter(k => activeFilterState[k] !== null);
    const isReady = FEATURES.MAINTENANCE_SALARY_FILTERS.every(key => selectedFilterKeys.includes(key));

    if (isReady) {
      const prefilledDetails = getFiltersAsDetails();
      setSelectedRateItem({
        category: "",
        salary: "",
        allowance: 0,
        details: prefilledDetails
      });
      setRateModal({ isOpen: true, mode: "Add" });
    } else {
      showGeneralMessage("Please fill up all filters first", false);
    }
  };
  const handleCloseRateModal = () => {
    setRateModal(initialModalState);
    setSelectedRateItem(null);
  };

  // -- Operation Benefit Handlers --
  const handleBenefitEdit = (item) => {
    setSelectedBenefitItem(item);
    setOperationBenefitModal({ isOpen: true, mode: "Edit" });
  };

  const handleBenefitSave = async (form) => {
    const res = await saveBenefit(selectedBenefitItem?.id, form, user.id);
    if (res.success) setOperationBenefitModal(initialModalState);
  };

  const handleOpenBenefitModal = () => {
    const selectedFilterKeys = Object.keys(activeFilterState).filter(k => activeFilterState[k] !== null);
    const isReady = FEATURES.MAINTENANCE_SALARY_FILTERS.every(key => selectedFilterKeys.includes(key));

    if (isReady) {
      // MODIFIED: No longer hardcoding headerId, user selects in modal dropdown
      if (!rules || rules.length === 0) {
        return showGeneralMessage("You must create at least one Rate Rule for this combination before adding benefits.", false);
      }

      setSelectedBenefitItem({
        rate: 0,
        operationBenefitGroupId: "",
        maintenanceSalaryCombinationV2Id: "" // MODIFIED: Start empty for user selection
      });
      setOperationBenefitModal({ isOpen: true, mode: "Add" });
    } else {
      showGeneralMessage("Please fill up all filters first", false);
    }
  };
  const handleCloseOperationBenefitModal = () => {
    setOperationBenefitModal(initialModalState);
    setSelectedBenefitItem(null);
  };
  // -- Operation Benefit Group Handlers --
  const handleOpenOperationBenefitGroupModal = () => {
    setOperationBenefitGroupModalOpen({ isOpen: true, mode: "Add" });
  }
  const handleOperationBenefitGroupEdit = (item) => {
    setSelectedOperationBenefitGroupItem(item);
    setOperationBenefitGroupModalOpen({ isOpen: true, mode: "Edit" });
  };
  const handleCloseOperationBenefitGroupModal = (item) => {
    setSelectedOperationBenefitGroupItem(null);
    setOperationBenefitGroupModalOpen(initialModalState);
  };
  const handleOperationBenefitGroupSave = async (form) => {
    const res = await saveGroup(selectedOperationBenefitGroupItem?.id, form)
    if (res.success) {
      setOperationBenefitGroupModalOpen(initialModalState);
      await refresh();
    }
  }


  const EmptyDashboardState = () => (
    <div className="w-full flex flex-col items-center justify-center py-16 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl mx-3">
      <div className="bg-white p-2 rounded-full shadow-sm mb-2">
        <FilterAltIcon className="text-gray-300" sx={{ fontSize: 25 }} />
      </div>
      <h3 className="text-gray-600 font-bold text-lg mb-1">No Data to Display</h3>
      <p className="text-gray-400 text-sm max-w-xs text-center">
        Apply filter criteria above to load salary rules and linked operation benefits.
      </p>
    </div>
  );

  return (
    <>
      <div className='p-4 space-y-6 relative'>
        {/* 1. Dynamic Filter Bar Section */}
        <div className="py-0 px-3">
          <div className="bg-white rounded-lg border border-gray-200 mt-2 p-4" style={{ border: "1px solid #d7d7d7" }}>
            <span className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4 block">Filter Rates</span>
            <DynamicFilterBar
              requestedFilters={FEATURES.MAINTENANCE_SALARY_FILTERS}
              activeFilterState={activeFilterState}
              onFilterChange={onFilterChange}
              onApply={applyFilters}
              onClear={resetFilters}
              allowAll={false}
            />
          </div>
        </div>

        {/* 2. Action Buttons */}
        <div className="flex justify-end items-center gap-2 px-3">
          <Button
            icon={<AddCardIcon />}
            name={"New Rate"}
            className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer shadow-md"}
            onClick={handleOpenRateModal}
          />
          <Button
            icon={<PostAddIcon />}
            name={"New Operation Benefit"}
            className={"bg-blue-600 hover:bg-blue-700 border-none cursor-pointer shadow-md"}
            onClick={handleOpenBenefitModal}
          />
          <Button
            icon={<LayersIcon />}
            name={"New Operation Benefit Group"}
            className={"bg-blue-600 hover:bg-blue-700 border-none cursor-pointer shadow-md"}
            onClick={handleOpenOperationBenefitGroupModal}
          />
        </div>

        {/* 3. Horizontal Dashboard (Rates & Operation Benefits) */}
        <div className="flex flex-row gap-6 overflow-x-auto pb-6 px-3 custom-horizontal-scrollbar items-start">
          {rules.length > 0 ? (
            <>
              <div className="min-w-[450px] flex-shrink-0 bg-white p-5 rounded-xl">
                <div className="mb-4 border-b border-gray-100 pb-3">
                  <h4 className="m-0 text-gray-600 font-extrabold uppercase tracking-tight text-sm">Employee Rate Table</h4>
                </div>
                <MaintenanceSalaryCombinationV2Table
                  data={rules}
                  onEdit={handleRateEdit}
                  loading={loading}
                  currentPage={currentRatePage}
                  setCurrentPage={setCurrentRatePage}
                />
              </div>

              {benefitGroups?.map((group) => (
                <div key={group.id} className="min-w-[400px] flex-shrink-0 p-5 rounded-xl">
                  <div className="mb-4 border-b border-gray-200 pb-3 flex justify-between items-center">
                    <h4 className="m-0 flex items-center text-blue-800 font-extrabold uppercase cursor-pointer tracking-tight text-sm" onClick={() => handleOperationBenefitGroupEdit(group)}>
                      {group.description}
                      <span className="ml-1 text-green-500 hover:text-green-700">
                        <EditIcon fontSize="small" />
                      </span>
                    </h4>

                    <div className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded text-[9px] font-bold">
                      BENEFITS
                    </div>
                  </div>
                  <OperationBenefitGroupTable
                    data={group.benefits || []}
                    onEdit={handleBenefitEdit}
                    loading={loading}
                  />
                </div>
              ))}
            </>
          ) :  <EmptyDashboardState />}
        </div>
      </div>

      <RateAddEditModal
        mode={rateModal.mode}
        isOpen={rateModal.isOpen}
        onClose={handleCloseRateModal}
        selectedItem={selectedRateItem}
        onSave={handleRateSave}
      />

      <OperationBenefitsAddEditModal
        mode={operationBenefitModal.mode}
        isOpen={operationBenefitModal.isOpen}
        groupOptions={groupOptions}
        loading={groupsLoading}
        onClose={handleCloseOperationBenefitModal}
        selectedItem={selectedBenefitItem}
        onSave={handleBenefitSave}
        rules={rules}
      />

      <OperationBenefitGroupModal
        mode={operationBenefitGroupModal.mode}
        isOpen={operationBenefitGroupModal.isOpen}
        selectedItem={selectedOperationBenefitGroupItem}
        onClose={handleCloseOperationBenefitGroupModal}
        onSave={handleOperationBenefitGroupSave}
      />
    </>
  );
};

export default MaintenanceSalaryCombinationV2Module;